import { Component } from '@theme/component';

/**
 * Infinite loop carousel for brand logos.
 * Clones logo items and calculates exact scroll distance for a seamless loop.
 * Pauses on hover and respects prefers-reduced-motion.
 *
 * @extends Component<{ carousel: HTMLElement, track: HTMLElement }>
 */
class PvBrandLogos extends Component {
  requiredRefs = ['carousel', 'track'];

  #prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    super.connectedCallback();
    this.#initCarousel();
  }

  #initCarousel() {
    const { carousel, track } = this.refs;
    if (track.dataset.initialized) return;

    const items = Array.from(track.querySelectorAll('.pv-brands__logo-item'));
    if (items.length === 0) return;

    const originalCount = items.length;

    // Clone items until the track is at least 2x the container width
    let safety = 0;
    do {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
      safety++;
    } while (track.scrollWidth < carousel.offsetWidth * 2 && safety < 10);

    // Measure exact distance: the offsetLeft of the first clone is exactly
    // how far we need to scroll before resetting, giving a seamless loop.
    const firstClone = track.children[originalCount];
    const scrollDist = firstClone.offsetLeft;
    track.style.setProperty('--pv-scroll-dist', `-${scrollDist}px`);

    track.dataset.initialized = 'true';

    // Pause on hover (only when motion is allowed)
    if (!this.#prefersReduced) {
      carousel.addEventListener('mouseenter', () => {
        track.style.animationPlayState = 'paused';
      });
      carousel.addEventListener('mouseleave', () => {
        track.style.animationPlayState = 'running';
      });
    }
  }
}

if (!customElements.get('pv-brand-logos')) {
  customElements.define('pv-brand-logos', PvBrandLogos);
}
